import { useState, useEffect, createContext, useContext } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import { HEADER, NAVBAR } from '../../config';
import useAuth from '../../hooks/useAuth';
import { useCampus } from '../../hooks/useCampus';
import NavbarVertical from '../dashboard/navbar/NavbarVertical';
import DashboardHeader from '../dashboard/header';
import MainFooter from './MainFooter';
import MainHeader from './MainHeader';
import MobileBottomNav from '../dashboard/header/MobileBottomNav';
import AdminBottomNav from '../dashboard/AdminMobileNav';
import TemplateProvider from '../../templates/TemplateProvider';
import EmailVerificationPrompt from '../../components/EmailVerificationPrompt';
import { AdminOnboardingProvider } from '../../contexts/AdminOnboardingContext';
import SalesAssistFab from '../../components/SalesAssistFab';
import SpotlightProvider from '../../components/spotlight/SpotlightProvider';
import { takeResumablePayment } from '../../utils/paymentReconciliation';

// Exposes sidebar open/close to any descendant (e.g. UserDashboard avatar button).
const SidebarContext = createContext({ setOpen: () => {} });
export const useSidebar = () => useContext(SidebarContext);

// ── Return-to-Atlas banner ────────────────────────────────────────────────────
function ReturnToAtlasBanner() {
  const navigate = useNavigate();
  const isAtlasSession = localStorage.getItem('atlas_session') === 'true';

  if (!isAtlasSession) return null;

  return (
    <Box
      onClick={() => navigate('/atlas/dashboard')}
      sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        height: 36,
        background: `linear-gradient(90deg, #D4A853 0%, #E8C27A 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        '&:hover': { filter: 'brightness(1.07)' },
        transition: 'filter 0.15s',
        gap: 1.5,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6, height: 6, borderRadius: '50%',
          bgcolor: '#0B0E14', opacity: 0.6,
          animation: 'pulse 1.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 0.4 },
            '50%': { opacity: 1 },
          },
        }}
      />
      <Typography sx={{
        color: '#0B0E14', fontWeight: 700, fontSize: 12,
        letterSpacing: 0.5,
      }}>
        Atlas session active — Return to Atlas →
      </Typography>
    </Box>
  );
}

// ----------------------------------------------------------------------

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: HEADER.MOBILE_HEIGHT + 24,
  paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    paddingBottom: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    // width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    // marginLeft: 158,
    // ...(collapseClick && {
    //   marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    // }),
  },
}));

const AuthMainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: HEADER.MOBILE_HEIGHT,
  paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    paddingBottom: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
}));

export default function MainLayout() {
  const { user } = useAuth();

  if (!user) return <AnonUserLayout />;

  switch (user.role) {
    case 'user':
      return <UserLayout />;
    case 'org':
      return <OrgLayout />;
    case 'restaurant_admin':
      return <RestaurantLayout />;
    case 'chef':
      return <ChefLayout />;
    case 'kiosk':
      return <KioskLayout />;
    default:
      return <OrgLayout />;
  }
}

export function UserLayout() {
  const { collapseClick, isCollapse } = useCollapseDrawer();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:1199px)');

  // The app was restarted mid-payment (iOS closes it while the member is in
  // GPay) and came up on the home page. Send them back to the checkout, which
  // confirms the payment against the server and shows what they bought.
  useEffect(() => {
    if (location.pathname.includes('checkout')) return;
    const resumeAt = takeResumablePayment();
    if (resumeAt) navigate(resumeAt, { replace: true });
    // Once, when the member area first loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Always initialize campus — DashboardHeader (which also calls useCampus) is
  // hidden on mobile, so without this call activeCampus never lands in localStorage
  // and every campus-scoped API call gets "Campus Id is required".
  useCampus();

  // Header is always hidden on mobile — the dashboard embeds its own
  // avatar/header row, and other pages use the back button or bottom nav.
  const shouldHideDashboardHeader = isMobile;

  const hideBottomNavPages = [
    '/user/checkout',
    '/user/meeting-room',
    '/user/meeting-room/book',
    '/user/meeting-room/checkout',
    '/user/mealos/cart',
    '/user/printing/new',
    '/user/printing/checkout',
    '/user/seat-selector',
  ];
  const shouldHideBottomNav =
    hideBottomNavPages.includes(location.pathname) ||
    // Full-screen booking flows draw their own action bar pinned to the bottom
    // of the viewport; the nav sits on top of it and buries Continue.
    location.pathname.startsWith('/user/meeting-room/extend/') ||
    location.pathname.startsWith('/user/rooms/extend/') ||
    location.pathname.startsWith('/user/mealos/menu/') ||
    location.pathname.startsWith('/user/mealos/order/track/') ||
    location.pathname.startsWith('/user/mealos/payment/') ||
    location.pathname.startsWith('/user/mealos/company-credits');

  return (
    <SidebarContext.Provider value={{ setOpen }}>
      <Box sx={{ display: { lg: 'flex' }, minHeight: { lg: 1 } }}>
        {!shouldHideDashboardHeader && (
          <DashboardHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />
        )}
        <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />

        <AuthMainStyle
          collapseClick={collapseClick}
          sx={{
            pb: { xs: `${HEADER.MOBILE_HEIGHT + 24}px`, lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px` },
            pt: { xs: 0, lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT}px` },
          }}
        >
          <TemplateProvider>
            <Outlet />
          </TemplateProvider>
          <Box sx={{ flexGrow: 1 }} />
        </AuthMainStyle>

        {!shouldHideBottomNav && <MobileBottomNav header={HEADER} />}
      </Box>
    </SidebarContext.Provider>
  );
}

export function OrgLayout() {
  const { collapseClick, isCollapse, onToggleCollapse, onHoverEnter, onHoverLeave } = useCollapseDrawer();
  const [open, setOpen] = useState(false);

  const { user } = useAuth();
  const { pathname } = useLocation();
  const isPhone = useMediaQuery('(max-width:599.95px)');

  // The floor plan builder is a canvas tool: it needs the whole window, and its
  // own bars sit at the screen edges. Left inside the normal shell, the app
  // header covers its toolbar and the sidebar eats the plan.
  const isFloorPlanBuilder = /^\/admin\/space-management\/floor-plans-v2\/[^/]+$/.test(pathname);

  if (!user) return <AnonUserLayout />;

  // Full-window tools render without sidebar and header.
  if (isFloorPlanBuilder) {
    return (
      <Box sx={{ minHeight: 1 }}>
        <Outlet />
      </Box>
    );
  }

  const isAtlasSession = localStorage.getItem('atlas_session') === 'true';

  return (
    <AdminOnboardingProvider>
    <SpotlightProvider>
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
        mt: isAtlasSession ? '36px' : 0,
      }}
    >
      <ReturnToAtlasBanner />
      <EmailVerificationPrompt />
      <DashboardHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />
      <NavbarVertical
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)}
        isCollapse={isCollapse}
        onToggleCollapse={onToggleCollapse}
        onHoverEnter={onHoverEnter}
        onHoverLeave={onHoverLeave}
      />

      <AuthMainStyle
        collapseClick={collapseClick}
        sx={
          isPhone
            // 88 = floating dock (60) + its 12px bottom offset + 16px breathing room
            ? { pb: `calc(${HEADER.MOBILE_HEIGHT + 24 + 88}px + env(safe-area-inset-bottom))` }
            : undefined
        }
      >
        <Outlet />
        <Box sx={{ flexGrow: 1 }} />

        {/* <MainFooter /> */}
      </AuthMainStyle>

      {isPhone && <AdminBottomNav />}
      {/* Free-wedge operators have no account manager. Renders null for
          everyone else. */}
      <SalesAssistFab />
      {/* <FloatingChatbot /> */}
    </Box>
    </SpotlightProvider>
    </AdminOnboardingProvider>
  );
}

export function ChefLayout() {
  const { collapseClick, isCollapse, onToggleCollapse, onHoverEnter, onHoverLeave } = useCollapseDrawer();
  const [open, setOpen] = useState(false);

  const { user } = useAuth();
  const { pathname } = useLocation();

  if (!user) return <AnonUserLayout />;

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
      }}
    >
      <DashboardHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />
      <NavbarVertical
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)}
        isCollapse={isCollapse}
        onToggleCollapse={onToggleCollapse}
        onHoverEnter={onHoverEnter}
        onHoverLeave={onHoverLeave}
      />

      <AuthMainStyle collapseClick={collapseClick}>
        <Outlet />
        <Box sx={{ flexGrow: 1 }} />

        {/* <MainFooter /> */}
      </AuthMainStyle>

      {/* <FloatingChatbot /> */}
    </Box>
  );
}

export function RestaurantLayout() {
  const { collapseClick, isCollapse, onToggleCollapse, onHoverEnter, onHoverLeave } = useCollapseDrawer();
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
      }}
    >
      <DashboardHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />
      <NavbarVertical
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)}
        isCollapse={isCollapse}
        onToggleCollapse={onToggleCollapse}
        onHoverEnter={onHoverEnter}
        onHoverLeave={onHoverLeave}
        // navConfig={navConfig}
      />

      <AuthMainStyle collapseClick={collapseClick}>
        <Outlet />
        <Box sx={{ flexGrow: 1 }} />
      </AuthMainStyle>
    </Box>
  );
}

export function AnonUserLayout() {
  const { collapseClick } = useCollapseDrawer();
  const [open, setOpen] = useState(false);

  return (
    <Stack sx={{ minHeight: 1 }}>
      {/* <MainHeader isCollapse={collapseClick} onOpenSidebar={() => setOpen(true)} /> */}
      <Outlet />
      {/* <MainStyle collapseClick={collapseClick}>
              <Outlet />
      </MainStyle> */}

      {/* <MainFooter /> */}
    </Stack>
  );
}

export function KioskLayout() {
  const { collapseClick } = useCollapseDrawer();
  const [open, setOpen] = useState(false);

  return (
    <Stack sx={{ minHeight: 1 }}>
      {/* <MainHeader isCollapse={collapseClick} onOpenSidebar={() => setOpen(true)} /> */}
      <Outlet />
      {/* <MainStyle collapseClick={collapseClick}>
              <Outlet />
      </MainStyle> */}

      {/* <MainFooter /> */}
    </Stack>
  );
}
